import { useEffect, useRef, useState } from 'react';

type UsePageScrollStateOptions = {
  scroll_settle_delay_ms?: number;
  scroll_start_delay_ms?: number;
};

const DEFAULT_PAGE_SCROLL_SETTLE_DELAY_MS = 420;
const DEFAULT_PAGE_SCROLL_START_DELAY_MS = 120;

export function use_page_scroll_state({
  scroll_settle_delay_ms = DEFAULT_PAGE_SCROLL_SETTLE_DELAY_MS,
  scroll_start_delay_ms = DEFAULT_PAGE_SCROLL_START_DELAY_MS,
}: UsePageScrollStateOptions = {}) {
  const [is_page_scrolling, set_is_page_scrolling] = useState(false);
  const is_page_scrolling_ref = useRef(false);

  useEffect(() => {
    let scroll_start_timeout_id: number | null = null;
    let scroll_settle_timeout_id: number | null = null;

    const set_page_scroll_state = (next_is_page_scrolling: boolean) => {
      is_page_scrolling_ref.current = next_is_page_scrolling;
      set_is_page_scrolling(next_is_page_scrolling);
    };

    const clear_scroll_start_timeout = () => {
      if (!scroll_start_timeout_id) {
        return;
      }

      window.clearTimeout(scroll_start_timeout_id);
      scroll_start_timeout_id = null;
    };

    const clear_scroll_settle_timeout = () => {
      if (!scroll_settle_timeout_id) {
        return;
      }

      window.clearTimeout(scroll_settle_timeout_id);
      scroll_settle_timeout_id = null;
    };

    const activate_scroll_state = () => {
      scroll_start_timeout_id = null;
      set_page_scroll_state(true);
    };

    const schedule_scroll_start_timeout = () => {
      if (is_page_scrolling_ref.current || scroll_start_timeout_id) {
        return;
      }

      scroll_start_timeout_id = window.setTimeout(
        activate_scroll_state,
        scroll_start_delay_ms,
      );
    };

    const settle_scroll_state = () => {
      clear_scroll_start_timeout();
      set_page_scroll_state(false);
    };

    const handle_scroll_event = () => {
      clear_scroll_settle_timeout();
      schedule_scroll_start_timeout();
      scroll_settle_timeout_id = window.setTimeout(
        settle_scroll_state,
        scroll_settle_delay_ms,
      );
    };

    window.addEventListener('scroll', handle_scroll_event, { passive: true });

    return () => {
      clear_scroll_start_timeout();
      clear_scroll_settle_timeout();
      window.removeEventListener('scroll', handle_scroll_event);
    };
  }, [scroll_settle_delay_ms, scroll_start_delay_ms]);

  return is_page_scrolling;
}
